import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import api from "@/lib/api";
import { track } from "@/lib/track";
import Header from "@/components/Header";
import MatchScoreRing from "@/components/MatchScoreRing";
import { useAuth, useIsAdmin } from "@/lib/auth";
import AdminQuickActions from "@/components/AdminQuickActions";
import StatusBadges from "@/components/StatusBadges";
import { toast } from "sonner";
import { ArrowRight, Calendar, MapPin, DollarSign, Building2, Lock, EyeOff, Eye, ShieldAlert, AlertCircle } from "lucide-react";

// Maps the raw `salary_band` field to a display string, applying the same
// "commensurate with experience" fallback the Market Intel endpoint uses so
// job pages don't render as "$—" or "$Unspecified".
const _COMP_FLOORS = {
  "D1:hc": 85, "D1:coord": 60, "D1:asst": 50,
  "D2:hc": 55, "D2:coord": 40, "D2:asst": 32,
  "D3:hc": 45, "D3:coord": 32, "D3:asst": 28,
  "NAIA:hc": 42, "NJCAA:hc": 40,
  "VARSITY:hc": 55, "CLUB:hc": 20,
};
function _displaySalary(job) {
  const raw = (job.salary_band || "").trim();
  const jt = String(job.job_type || "").toLowerCase();
  if (jt.includes("volunteer")) return "Unpaid";
  if ((jt.includes("stipend") || jt.includes("part")) && (job.stipend || "").trim()) {
    const s = job.stipend.trim();
    return `Stipend · ${s.startsWith("$") ? s : `$${s}`}`;
  }
  if (raw && !/commensurate|negotiable|doe/i.test(raw)) return raw.startsWith("$") ? raw : `$${raw}`;
  if (jt.includes("stipend") || jt.includes("part")) return "Stipend · commensurate with experience";
  const div = String(job.division || "").toUpperCase();
  const role = String(job.role || "").toLowerCase();
  const bucket = role.includes("head") && !role.includes("assistant") ? "hc"
    : (role.includes("coord") ? "coord" : "asst");
  const floor = _COMP_FLOORS[`${div}:${bucket}`] || 30;
  return `$${floor}k+ · commensurate with experience`;
}

export default function JobDetail() {
  const { jobId } = useParams();
  const { user } = useAuth();
  const isAdmin = useIsAdmin();
  const [job, setJob] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [matches, setMatches] = useState([]);
  const [suggested, setSuggested] = useState(null);

  const inviteCandidate = async (c) => {
    try {
      await api.post(`/admin/jobs/${jobId}/suggested/${c.coach_profile_id}/invite`);
      setSuggested((s) => ({ ...s, candidates: s.candidates.map((x) => x.coach_profile_id === c.coach_profile_id ? { ...x, invited: true } : x) }));
      toast.success(`Invite drafted for ${c.name} — review it in the Email Approval Console`);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Invite failed");
    }
  };
  const [loadingMatches, setLoadingMatches] = useState(true);

  useEffect(() => {
    track("job_page_visit");
    api.get(`/jobs/${jobId}`).then(({ data }) => setJob(data)).catch(() => setNotFound(true));
    api.get(`/jobs/${jobId}/matches`).then(({ data }) => { setMatches(data); setLoadingMatches(false); }).catch(() => setLoadingMatches(false));
  }, [jobId]);

  if (notFound) return <Shell><div className="p-10"><div className="heading text-3xl" data-testid="job-not-found">JOB NOT FOUND</div><Link to="/coach/opportunities" className="text-volt text-sm mt-3 inline-block">← Back to opportunities</Link></div></Shell>;
  if (!job) return <Shell><div className="p-10 heading text-3xl">LOADING…</div></Shell>;

  return (
    <Shell>
      <div className="px-4 md:px-8 lg:px-12 py-8" data-testid="job-detail-page">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
          <div className="lg:col-span-8">
            <div className="text-xs tracking-widest text-volt" data-testid="job-program-label">{(/wom|girl/i.test(job.gender || "") ? "WOMEN'S PROGRAM" : /men|boy/i.test(job.gender || "") ? "MEN'S PROGRAM" : /coed/i.test(job.gender || "") ? "COED PROGRAM" : "PROGRAM UNSPECIFIED")} · {job.sport?.toUpperCase()} · {job.division || job.level}</div>
            <h1 className="heading text-4xl md:text-5xl lg:text-6xl mt-2">{job.title}</h1>
            <div className="text-neutral-400 mt-2 flex items-center gap-3 flex-wrap">
              <span>{job.program_name}</span>
              <StatusBadges adFounder={job.poster_badges?.ad_founder} />
            </div>
            <div className="mt-3">
              <AdminQuickActions
                entity="job"
                id={job.id}
                name={job.title}
                fields={[
                  { key: "title", label: "Title", value: job.title },
                  { key: "program_name", label: "Program / School", value: job.program_name },
                  { key: "location", label: "Location", value: job.location },
                  { key: "city", label: "City", value: job.city },
                  { key: "state", label: "State", value: job.state },
                  { key: "division", label: "Division", value: job.division },
                  { key: "level", label: "Level", value: job.level },
                  { key: "role", label: "Role", value: job.role },
                  { key: "salary_band", label: "Salary band", value: job.salary_band },
                  { key: "application_url", label: "Application URL", value: job.application_url },
                  { key: "contact_email", label: "Contact email", value: job.contact_email },
                  { key: "description", label: "Description", value: job.description, multiline: true },
                ]}
                onSaved={(updated) => setJob({ ...job, ...updated })}
              />
            </div>
            <div className="mt-4 flex flex-wrap gap-3 text-sm items-center">
              {job.job_type && (
                <span
                  data-testid="job-compensation-badge"
                  className={
                    "px-2.5 py-1 text-[10px] tracking-widest heading " +
                    (String(job.job_type).toLowerCase().includes("volunteer")
                      ? "bg-neutral-800 text-neutral-300"
                      : String(job.job_type).toLowerCase().includes("stipend") ||
                        String(job.job_type).toLowerCase().includes("part")
                        ? "bg-volt text-black"
                        : "bg-blaze text-white")
                  }
                >
                  {String(job.job_type).toUpperCase()}
                </span>
              )}
              {(String(job.job_type || "").toLowerCase().includes("part") || String(job.job_type || "").toLowerCase().includes("stipend")) && job.stipend && (
                <span data-testid="job-stipend-badge" className="px-2.5 py-1 text-[10px] tracking-widest heading border border-volt text-volt">
                  STIPEND · {String(job.stipend).toUpperCase()}
                </span>
              )}
              <Meta icon={MapPin}>{job.location || job.region}</Meta>
              <Meta icon={DollarSign}>{_displaySalary(job)}</Meta>
              <Meta icon={Building2}>{job.setting} · {job.selectivity}</Meta>
              {job.start_date && <Meta icon={Calendar}>Start {job.start_date}</Meta>}
              {job.position_timing && (
                <Meta icon={Calendar}>{job.position_timing === "next_season" ? "Starts after the current season" : "Available immediately"}</Meta>
              )}
            </div>
          </div>
          <div className="lg:col-span-4 border border-neutral-800 p-6 bg-neutral-950">
            <div className="text-xs tracking-widest text-blaze">PROGRAM NEEDS (TKG)</div>
            <div className="mt-3 space-y-2 text-sm">
              <Need label="Status" value={job.needs?.program_status} />
              <Need label="Philosophy" value={job.needs?.philosophy_priority} />
              <Need label="Culture" value={job.needs?.cultural_fit} />
              <Need label="Academic" value={job.needs?.academic_focus} />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="mt-8 border border-neutral-800 p-6 bg-appcard">
          <div className="text-xs tracking-widest text-neutral-500 mb-3">JOB DESCRIPTION</div>
          <div className="text-neutral-300 leading-relaxed whitespace-pre-line">{job.description}</div>
        </div>

        {/* Admin-only full-view panel with paywall highlighting */}
        {isAdmin && <AdminJobFullView job={job} />}

        {/* Admin-only suggested candidates */}
        {isAdmin && (
          <div className="mt-10 border border-volt/40 bg-appcard p-6" data-testid="suggested-candidates-panel">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div>
                <div className="text-xs tracking-widest text-volt">PLATFORM ADMIN ONLY · NOT VISIBLE TO COACHES OR ADs</div>
                <h2 className="heading text-2xl md:text-3xl mt-1">SUGGESTED CANDIDATES</h2>
              </div>
              {!suggested && (
                <button
                  data-testid="load-suggested-btn"
                  onClick={() => api.get(`/admin/jobs/${jobId}/suggested`).then(({ data }) => setSuggested(data)).catch(() => setSuggested({ candidates: [] }))}
                  className="min-h-[44px] px-5 border border-volt text-volt heading text-sm tracking-widest hover:bg-volt hover:text-white transition-colors"
                >
                  RUN SHORTLIST
                </button>
              )}
            </div>
            {suggested && (
              <>
                <div className="text-[10px] tracking-widest text-neutral-500 mt-2">
                  RANKED BY CIR AT TARGET BAR {suggested.target_level} · TOP {suggested.candidates.length}
                </div>
                <div className="mt-4 space-y-2">
                  {suggested.candidates.length === 0 && <div className="text-neutral-500 text-sm">No eligible coaches found.</div>}
                  {suggested.candidates.map((c, i) => (
                    <Link
                      key={c.coach_profile_id}
                      to={`/coaches/${c.coach_profile_id}?job=${jobId}`}
                      data-testid={`suggested-candidate-${c.coach_profile_id}`}
                      className="flex items-center gap-4 border border-neutral-800 px-4 py-3 hover:border-volt transition-colors"
                    >
                      <div className="text-mono text-neutral-600 w-6 shrink-0">{i + 1}</div>
                      <div className="min-w-0 flex-1">
                        <span className="heading text-lg">{c.name}</span>
                        {c.claim_state !== "claimed" && <span className="text-volt text-[9px] tracking-widest ml-2 align-middle">UNCLAIMED</span>}
                        <div className="text-neutral-400 text-xs truncate">{c.current_role || "—"} · {c.current_organization || "No current program"}</div>
                      </div>
                      <div className="text-right shrink-0">
                        <div className="heading text-xl text-volt">{c.score}</div>
                        <div className="text-[9px] tracking-widest text-neutral-500">{c.tier}</div>
                      </div>
                      {c.has_email && (
                        <button
                          data-testid={`invite-candidate-${c.coach_profile_id}`}
                          disabled={c.invited}
                          onClick={(e) => { e.preventDefault(); e.stopPropagation(); inviteCandidate(c); }}
                          className={`min-h-[36px] px-3 text-[10px] tracking-widest shrink-0 transition-colors ${
                            c.invited ? "border border-neutral-700 text-neutral-500" : "border border-blaze text-blaze hover:bg-blaze hover:text-white"
                          }`}
                        >
                          {c.invited ? "INVITED" : "INVITE"}
                        </button>
                      )}
                    </Link>
                  ))}
                </div>
              </>
            )}
          </div>
        )}

        {/* Matches */}
        <div className="mt-10">
          <div className="text-xs tracking-widest text-blaze">MATCHENGINEAGENT · XAI GROUNDED IN TKG</div>
          <h2 className="heading text-3xl md:text-4xl mt-2">CANDIDATE MATCHES</h2>

          {loadingMatches ? (
            <div className="mt-6 heading text-2xl text-neutral-500">RUNNING MATCH ENGINE…</div>
          ) : matches.length === 0 ? (
            <div className="mt-6 border border-dashed border-neutral-800 p-10 text-neutral-500">No coaches in the pool yet. Try seeding the demo coaches from the AD dashboard.</div>
          ) : (
            <div className="mt-6 space-y-4">
              {matches.map((m) => (
                <div key={m.id} className="border border-neutral-800 bg-appcard p-6" data-testid={`match-card-${m.coach_profile_id}`}>
                  <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">
                    <div className="md:col-span-2 flex md:flex-col items-center md:items-start gap-4">
                      <MatchScoreRing score={m.score} size={110} stroke={10} />
                      <div className="text-mono text-[10px] text-neutral-500">CIR {m.rationale?.qbr?.score ?? "—"} · HEUR {m.heuristic_score}</div>
                    </div>
                    <div className="md:col-span-10">
                      <div className="flex items-start justify-between gap-4 flex-wrap">
                        <div>
                          <div className="heading text-2xl">{m.coach_name} {m.is_player && <span className="bg-volt text-white text-[10px] px-1.5 py-0.5 ml-2 tracking-widest align-middle heading" data-testid={`player-badge-${m.id}`}>PLAYER</span>} {!m.claimed && <span className="text-volt text-xs ml-2 tracking-widest align-middle">[SCOUTED · UNCLAIMED]</span>}</div>
                          {m.coach_headline && <div className="text-neutral-400 text-sm mt-1">{m.coach_headline}</div>}
                        </div>
                        <Link to={`/coaches/${m.coach_profile_id}?job=${jobId}`} data-testid={`view-candidate-${m.coach_profile_id}`} className="flex items-center gap-2 bg-volt text-white heading text-base px-4 py-2 hover:glow-volt transition-all">
                          OPEN DOSSIER <ArrowRight size={14} />
                        </Link>
                      </div>
                      <p className="text-neutral-300 text-sm mt-3">{m.rationale?.summary}</p>
                      {m.rationale?.alignment_points && (
                        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
                          {m.rationale.alignment_points.map((a, i) => (
                            <div key={i} className="border-l-2 border-volt pl-3">
                              <div className="text-volt text-[10px] tracking-widest flex items-center justify-between">
                                <span>{a.title.toUpperCase()}</span>
                                <span className="text-mono">w {Number(a.weight).toFixed(2)}</span>
                              </div>
                              <div className="text-neutral-300 text-xs mt-1">{a.evidence}</div>
                            </div>
                          ))}
                        </div>
                      )}
                      {m.rationale?.watchouts?.length > 0 && (
                        <div className="mt-4 border-l-2 border-blaze pl-3">
                          <div className="text-blaze text-[10px] tracking-widest">WATCHOUTS</div>
                          <ul className="text-neutral-300 text-xs mt-1 list-disc pl-4">
                            {m.rationale.watchouts.map((w, i) => <li key={i}>{w}</li>)}
                          </ul>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </Shell>
  );
}

function Meta({ icon: Icon, children }) {
  return (
    <div className="flex items-center gap-1.5 text-neutral-400 border border-neutral-800 px-3 py-1.5">
      <Icon size={14} /> <span>{children}</span>
    </div>
  );
}
function Need({ label, value }) {
  return (
    <div className="flex items-start gap-3">
      <div className="text-[10px] tracking-widest text-neutral-500 mt-0.5 w-20 shrink-0">{label.toUpperCase()}</div>
      <div className="text-neutral-200 text-sm">{value || <span className="text-neutral-600">—</span>}</div>
    </div>
  );
}
function Shell({ children }) { return <div className="min-h-screen bg-appbg text-white"><Header />{children}</div>; }

/** Admin-only "full posting" view. Shows every populated field with a visual
 * badge indicating whether that field would be hidden from a free-tier coach
 * (paywalled) or a public viewer of an anonymous listing (stealth-masked).
 * Matches the redaction rules in server.py `_paywall_mask_job` /
 * `redact_job_for_viewer` so what admins see here reflects what the paywalls
 * actually enforce.
 */
function AdminJobFullView({ job }) {
  const isAnonymous = !!job.anonymous_listing;

  // Field groups + their visibility policy
  const fields = [
    { key: "title", label: "Title", value: job.title, gates: [] },
    { key: "program_name", label: "Program / School", value: job.program_name, gates: ["paywall", "stealth"] },
    { key: "role", label: "Role", value: job.role, gates: [] },
    { key: "location", label: "Location", value: job.location, gates: ["paywall", "stealth"] },
    { key: "city", label: "City", value: job.city, gates: ["paywall", "stealth"] },
    { key: "state", label: "State", value: job.state, gates: ["paywall", "stealth"] },
    { key: "sport", label: "Sport", value: job.sport, gates: [] },
    { key: "gender", label: "Gender", value: job.gender, gates: [] },
    { key: "level", label: "Level", value: job.level, gates: [] },
    { key: "division", label: "Division", value: job.division, gates: [] },
    { key: "conference", label: "Conference", value: job.conference, gates: ["stealth"] },
    { key: "job_type", label: "Job type", value: job.job_type, gates: [] },
    { key: "salary_band", label: "Salary band", value: job.salary_band, gates: ["stealth"] },
    { key: "salary_min", label: "Salary min", value: job.salary_min ? `$${job.salary_min}` : null, gates: ["stealth"] },
    { key: "salary_max", label: "Salary max", value: job.salary_max ? `$${job.salary_max}` : null, gates: ["stealth"] },
    { key: "compensation_type", label: "Compensation type", value: job.compensation_type, gates: [] },
    { key: "application_deadline", label: "Application deadline", value: job.application_deadline, gates: ["stealth"] },
    { key: "start_date", label: "Start date", value: job.start_date, gates: [] },
    { key: "position_timing", label: "Position timing", value: job.position_timing ? (job.position_timing === "next_season" ? "After current season" : "Immediate") : null, gates: [] },
    { key: "application_url", label: "Application URL", value: job.application_url, gates: ["stealth"] },
    { key: "contact_name", label: "Contact name", value: job.contact_name, gates: ["stealth"] },
    { key: "contact_email", label: "Contact email", value: job.contact_email, gates: ["stealth"] },
    { key: "contact_phone", label: "Contact phone", value: job.contact_phone, gates: ["stealth"] },
    { key: "setting", label: "Setting", value: job.setting, gates: ["stealth"] },
    { key: "selectivity", label: "Selectivity", value: job.selectivity, gates: ["stealth"] },
    { key: "enrollment", label: "Enrollment", value: job.enrollment, gates: ["stealth"] },
    { key: "scholarship_type", label: "Scholarship type", value: job.scholarship_type, gates: [] },
    { key: "nil_available", label: "NIL Available", value: job.nil_available === true ? "Yes" : job.nil_available === false ? "No" : null, gates: [] },
    { key: "region", label: "Region", value: job.region, gates: [] },
    { key: "posting_source", label: "Posting source", value: job.posting_source, gates: [] },
    { key: "source_url", label: "Source URL", value: job.source_url, gates: [] },
    { key: "is_faculty_position", label: "Faculty / teaching component", value: job.is_faculty_position, gates: [] },
    { key: "faculty_subjects_needed", label: "Teaching subject(s) required", value: (job.faculty_subjects_needed || []).join(", ") || null, gates: [] },
  ];
  const filled = fields.filter((f) => f.value !== undefined && f.value !== null && f.value !== "");
  const empty = fields.filter((f) => !(f.value !== undefined && f.value !== null && f.value !== ""));

  const missingContact = !job.contact_email && !job.contact_phone && !job.contact_name && !job.application_url;

  return (
    <div className="mt-8 border border-volt/40 bg-[#0A101A] p-6" data-testid="admin-job-full-view">
      <div className="flex items-center gap-2 text-xs tracking-widest text-volt heading">
        <ShieldAlert size={14} /> ADMIN · FULL POSTING VIEW
      </div>
      <p className="text-neutral-400 text-xs mt-1">
        Everything on this job. Fields marked with <Lock className="inline" size={11} /> are hidden from free-tier
        coaches; <EyeOff className="inline" size={11} /> means the field is stripped when a stealth/anonymous listing
        is viewed by anyone but the AD who posted it.
        {isAnonymous && <span className="text-blaze ml-2">· This listing is currently in STEALTH mode.</span>}
      </p>

      {missingContact && (
        <div className="mt-4 border border-blaze/50 bg-[#1a0a0a] px-4 py-3 flex items-start gap-2" data-testid="admin-job-missing-contact-warn">
          <AlertCircle size={14} className="text-blaze mt-0.5 shrink-0" />
          <div className="text-sm text-neutral-200">
            <div className="text-blaze text-[11px] tracking-widest heading mb-1">NO CONTACT INFORMATION</div>
            No contact_email, contact_phone, contact_name, or application_url is set. Coaches can&apos;t apply.
            <Link to={`/ad/marketing/jobs/${job.id}/edit`} className="ml-2 text-volt hover:underline">Fix in Full Editor →</Link>
          </div>
        </div>
      )}

      {/* Populated fields grid */}
      <div className="mt-5 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-2">
        {filled.map((f) => (
          <FieldRow key={f.key} f={f} isAnonymous={isAnonymous} />
        ))}
      </div>

      {/* Empty fields — collapsed by default */}
      {empty.length > 0 && (
        <details className="mt-5" data-testid="admin-job-empty-fields">
          <summary className="cursor-pointer text-[11px] tracking-widest text-neutral-500 hover:text-volt">
            + {empty.length} EMPTY FIELD{empty.length === 1 ? "" : "S"} (CLICK TO EXPAND)
          </summary>
          <div className="mt-3 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1">
            {empty.map((f) => (
              <div key={f.key} className="flex items-center gap-2 text-[11px] text-neutral-600 py-1">
                <span className="w-40 shrink-0">{f.label}</span>
                <span className="text-neutral-800">— unset</span>
                {f.gates.length > 0 && <GateBadges gates={f.gates} />}
              </div>
            ))}
          </div>
        </details>
      )}

      <div className="mt-6 pt-4 border-t border-neutral-800 flex items-center justify-between text-[11px] text-neutral-500">
        <span>
          <span className="text-neutral-300">{filled.length}</span> populated ·
          <span className="text-neutral-300 ml-1">{empty.length}</span> empty
        </span>
        <Link
          to={`/ad/marketing/jobs/${job.id}/edit`}
          data-testid="admin-job-open-full-editor"
          className="text-volt hover:text-white transition-colors flex items-center gap-1"
        >
          <Eye size={12} /> EDIT ALL FIELDS →
        </Link>
      </div>
    </div>
  );
}

function FieldRow({ f, isAnonymous }) {
  return (
    <div className="flex items-start gap-2 py-1.5 border-b border-neutral-900" data-testid={`admin-field-${f.key}`}>
      <span className="w-36 shrink-0 text-[11px] tracking-widest text-neutral-500 mt-0.5">{f.label.toUpperCase()}</span>
      <span className="flex-1 text-sm text-neutral-200 break-words">
        {String(f.value)}
      </span>
      {f.gates.length > 0 && <GateBadges gates={f.gates} active={isAnonymous ? ["stealth"] : []} />}
    </div>
  );
}

function GateBadges({ gates, active = [] }) {
  return (
    <div className="flex gap-1 shrink-0 mt-0.5">
      {gates.includes("paywall") && (
        <span
          title="Program details"
          className="inline-flex items-center gap-1 text-[9px] tracking-widest px-1.5 py-0.5 border border-amber-500/50 text-amber-400"
        >
          <Lock size={9} /> PAYWALL
        </span>
      )}
      {gates.includes("stealth") && (
        <span
          title="Stripped when this listing is viewed anonymously — only AD + admin see it in stealth mode"
          className={
            "inline-flex items-center gap-1 text-[9px] tracking-widest px-1.5 py-0.5 border " +
            (active.includes("stealth") ? "border-blaze text-blaze" : "border-neutral-700 text-neutral-500")
          }
        >
          <EyeOff size={9} /> STEALTH
        </span>
      )}
    </div>
  );
}

